import { Autocomplete, Box, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { DateRange, MobileTimeRangePicker } from '@mui/x-date-pickers-pro';

import type { PlanningData } from './planningData.types.ts';

import { GetBookableExpertenByThemaOutput } from '../../../dtos.ts';
import { getExperteName } from '../../../utils';
import { content } from '../CreateDurchgang.content.ts';

type CreateDurchgangDayInputsProps = Readonly<{
  modulIndex: number;
  dayIndex: number;
  planningData: PlanningData;
  resetDurchgangAvailability: () => void;
  setPlanningData: React.Dispatch<React.SetStateAction<PlanningData>>;
  expertePool: GetBookableExpertenByThemaOutput | undefined;
  useExpertenForAllDays: boolean;
  setUseExpertenForAllDays: React.Dispatch<React.SetStateAction<boolean>>;
}>;

export const CreateDurchgangDayInputs = ({
  modulIndex,
  dayIndex,
  planningData,
  resetDurchgangAvailability,
  setPlanningData,
  expertePool,
  useExpertenForAllDays,
  setUseExpertenForAllDays,
}: CreateDurchgangDayInputsProps) => {
  const handleExperteSelection = (values: number[], modulIndex: number, dayIndex: number) => {
    let module = [...planningData.module];
    module[modulIndex].termine[dayIndex].expertenSapIds = values;

    if (useExpertenForAllDays) {
      module = applyExpertenToAllModuleDays(planningData.module);
    }

    setPlanningData((prev) => ({ ...prev, module }));
  };

  const applyExpertenToAllModuleDays = (module: PlanningData['module']) => {
    const firstModulFirstDayExperten = module[0].termine[0].expertenSapIds;
    return module.map((modul, modulIndex) => ({
      ...modul,
      termine: modul.termine.map((termin, dayIndex) => {
        if (modulIndex === 0 && dayIndex === 0) {
          return termin; // Skip first day of first module
        } else {
          return {
            ...termin,
            expertenSapIds: firstModulFirstDayExperten,
          };
        }
      }),
    }));
  };

  const handleUseExpertenForAllDays = () => {
    if (!useExpertenForAllDays) {
      const module = applyExpertenToAllModuleDays(planningData.module);
      setPlanningData((prev) => ({ ...prev, module }));
    }
    setUseExpertenForAllDays(!useExpertenForAllDays);
  };

  const handleTimeRangeChange = (values: DateRange<Date>, modulIndex: number, dayIndex: number) => {
    const module = structuredClone(planningData.module);
    module[modulIndex].termine[dayIndex].uhrzeiten = {
      start: values[0],
      end: values[1],
    };
    setPlanningData((prev) => ({ ...prev, module }));
  };

  return (
    <Box display="grid" gridTemplateColumns="28px repeat(3, minmax(0,1fr))" columnGap={2} rowGap={1} mt={3}>
      <Box display="flex" gap={2} alignItems="center" maxWidth={360} gridColumn={2}>
        <Typography variant="labelLight" color="textTertiary" whiteSpace="nowrap" width="6ch">
          {dayIndex + 1}. Tag
        </Typography>
        {/* Picked mobile picker because it has better usability, UX said we can choose */}
        <MobileTimeRangePicker
          label={content.timeRange}
          sx={{
            width: '100%',
            '& > div': {
              height: '36px',
              p: 2,
              borderRadius: '8px !important',
              '& > fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.54) !important',
              },
            },
          }}
          onChange={(values) => {
            handleTimeRangeChange(values, modulIndex, dayIndex);
            resetDurchgangAvailability();
          }}
        />
      </Box>

      {expertePool !== undefined && (
        <Autocomplete
          id={`experte-input${modulIndex}-${dayIndex}`}
          size="small"
          multiple
          value={planningData.module[modulIndex].termine[dayIndex].expertenSapIds}
          onChange={(_, values) => {
            handleExperteSelection(values, modulIndex, dayIndex);
            resetDurchgangAvailability();
          }}
          options={expertePool.map((e) => e.experteSapId)}
          getOptionLabel={(experteSapId) => getExperteName(expertePool.find((e) => e.experteSapId === experteSapId))}
          renderInput={(params) => (
            <TextField {...params} placeholder={content.selectTrainer} label={content.trainer} />
          )}
          disabled={modulIndex > 0 && useExpertenForAllDays}
          sx={{ maxWidth: 360 }}
        />
      )}
      {expertePool !== undefined && modulIndex === 0 && dayIndex === 0 && (
        <FormControlLabel
          control={
            <Switch
              aria-label={content.forAllModules}
              onChange={() => {
                handleUseExpertenForAllDays();
                resetDurchgangAvailability();
              }}
              checked={useExpertenForAllDays}
            />
          }
          label={<Typography variant="labelMultilineLight">{content.forAllModules}</Typography>}
          sx={{ ml: 0, mt: 1, gridColumn: 3 }}
        />
      )}
    </Box>
  );
};
